import React from 'react'
import { useFolderAccess } from './useFolderAccess'
import { useFolderData, useFolderChildren } from './useFolderData'
import type { DiagramSource } from '../domains/dashboard/models'
import type { Role, ViewDefinition } from '../core/repository/DiagramRepository'

export type FolderDashboardData = {
  folder: {
    id: string
    name: string
    parentId: string | null
    orgId: string
    ownerUserId: string
    createdAt: string
    updatedAt: string
    domain?: string
    source?: 'native' | 'legacy'
    archived?: boolean
  }
  children: {
    folders: Array<{
      id: string
      name: string
      parentId: string | null
      orgId: string
      ownerUserId: string
      createdAt: string
      updatedAt: string
      domain?: string
      source?: DiagramSource
    }>
    diagrams: Array<{
      id: string
      name: string
      folderId: string
      orgId: string
      ownerUserId: string | null
      type: string
      icon?: string | null
      role?: Role
      source?: DiagramSource
      isTemplate: boolean
      thumbnailUrl: string | null
      createdAt: string
      updatedAt: string
      // Carries the artifact kind (people_chart / position_chart projections).
      // The org folder panel buckets charts into Org / Plans / Views from this,
      // so dropping it here files every chart under Views.
      viewDefinition?: ViewDefinition | null
    }>
  }
}

export function useFolderDashboard(folderId?: string) {
  const { data: accessInfo, isLoading: accessLoading, error: accessError } = useFolderAccess(folderId)
  const hasAccess = Boolean(accessInfo?.role && accessInfo.role !== 'none')
  const canWrite = Boolean(accessInfo?.canWrite)

  const { data: folderData, isLoading: folderLoading, error: folderError } = useFolderData(folderId)
  const { data: childrenData, isLoading: childrenLoading, error: childrenError } = useFolderChildren(folderId)

  const isLoading = accessLoading || folderLoading || childrenLoading
  const error = accessError || folderError || childrenError

  const data = React.useMemo(() => {
    if (!folderData || !childrenData) return null

    return {
      folder: folderData.folder,
      children: childrenData
    } as FolderDashboardData
  }, [folderData, childrenData])

  return {
    data,
    isLoading,
    error,
    hasAccess,
    canWrite
  }
}
